import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import client from "../lib/api";
import { Coins, Trophy, Medal, Gift, Plus, Trash, ArrowCounterClockwise, FloppyDisk, Crown, CurrencyDollar, Info } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

const Field = ({ label, children }) => (
  <div>
    <p className="text-[11px] uppercase tracking-[0.14em] text-[#9A968E] mb-1">{label}</p>
    {children}
  </div>
);

const Card = ({ icon: Icon, title, hint, children, testid }) => (
  <div className="border border-[#E9DDC6] rounded-[5px] overflow-hidden bg-white" data-testid={testid}>
    <div className="bg-[#FAF9F6] px-4 py-3 flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-[#5C5A58]">
      <Icon size={16} weight="duotone" className="text-[#B08D57]" />{title}
    </div>
    {hint && <p className="px-4 pt-3 text-xs text-[#9A968E]">{hint}</p>}
    <div className="p-4">{children}</div>
  </div>
);

// Editor for a SINGLE role's rewards program (points, tiers, conversion, store).
export default function RewardsStructure({ role = "agent" }) {
  const [rc, setRc] = useState(null);          // this role's config slice
  const [labels, setLabels] = useState({});    // this role's action labels
  const [defaults, setDefaults] = useState(null);
  const [meta, setMeta] = useState({});        // version / updated_at / updated_by
  const [metricOptions, setMetricOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = () =>
    client.get("/rewards/admin/config").then((r) => {
      const d = r.data;
      setRc(JSON.parse(JSON.stringify(d.roles[role])));
      setLabels((d.labels || {})[role] || {});
      setDefaults((d.defaults?.roles || {})[role] || null);
      setMetricOptions((d.metric_options || {})[role] || []);
      setMeta({ version: d.version, updated_at: d.updated_at, updated_by: d.updated_by });
    });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  if (!rc) return <p className="text-sm text-[#5C5A58]" data-testid="structure-loading">Loading structure&hellip;</p>;

  const setPts = (key, val) => setRc((c) => ({ ...c, pts: { ...c.pts, [key]: val } }));
  const setLevel = (i, field, val) => setRc((c) => { const levels = c.levels.map((l) => [...l]); levels[i][field] = val; return { ...c, levels }; });
  const addLevel = () => setRc((c) => ({ ...c, levels: [...c.levels, ["New tier", 0]] }));
  const removeLevel = (i) => setRc((c) => ({ ...c, levels: c.levels.filter((_, x) => x !== i) }));
  const setStore = (i, field, val) => setRc((c) => { const store = c.store.map((s) => ({ ...s })); store[i][field] = val; return { ...c, store }; });
  const addStore = () => setRc((c) => ({ ...c, store: [...c.store, { key: "", type: "credit", label: "", desc: "", points: 0, value: 0 }] }));
  const removeStore = (i) => setRc((c) => ({ ...c, store: c.store.filter((_, x) => x !== i) }));
  const setAch = (i, field, val) => setRc((c) => { const a = (c.achievements || []).map((x) => ({ ...x })); a[i][field] = val; return { ...c, achievements: a }; });
  const addAch = () => setRc((c) => ({ ...c, achievements: [...(c.achievements || []), { key: "", label: "", desc: "", metric: metricOptions[0]?.key || "", target: 1 }] }));
  const removeAch = (i) => setRc((c) => ({ ...c, achievements: (c.achievements || []).filter((_, x) => x !== i) }));

  const resetDefaults = () => {
    if (!defaults) return;
    setRc(JSON.parse(JSON.stringify(defaults)));
    toast.message("Defaults loaded — review, then Save to apply.");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { roles: { [role]: {
        pts: Object.fromEntries(Object.entries(rc.pts || {}).map(([k, v]) => [k, Number(v) || 0])),
        levels: rc.levels.map((l) => [String(l[0]), Number(l[1]) || 0]),
        points_per_dollar: Number(rc.points_per_dollar) || 10,
        store: rc.store.map((s) => ({
          key: s.key || "", type: s.type, label: s.label, desc: s.desc,
          points: Number(s.points) || 0, value: Number(s.value) || 0,
          ...(s.type === "penthouse" ? { months: Number(s.months) || 12 } : {}),
        })),
        achievements: (rc.achievements || []).map((a) => ({
          key: a.key || "", label: a.label, desc: a.desc,
          metric: a.metric, target: Math.max(1, Number(a.target) || 1),
        })),
      } } };
      await client.put("/rewards/admin/config", payload);
      toast.success("Rewards structure saved. New rates apply to future activity only.");
      setConfirm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the structure.");
    } finally { setSaving(false); }
  };

  const hasActions = Object.keys(labels).length > 0;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="rewards-structure">
      <div className="flex items-start gap-2 border border-[#B08D57]/40 bg-[#B08D57]/10 rounded-[5px] p-3 text-sm text-[#8a6a3a]">
        <Info size={16} weight="fill" className="text-[#B08D57] mt-0.5 shrink-0" />
        <span><b>Changes are not retroactive.</b> Editing points, tiers or the store only affects rewards earned from the moment you save — every member&rsquo;s already-earned points stay locked at the old rates.
          {meta.updated_at && <> Last edited {new Date(meta.updated_at).toLocaleDateString()}{meta.updated_by ? ` by ${meta.updated_by}` : ""}.</>}</span>
      </div>

      {/* Points per action */}
      <Card icon={Coins} title="Points per action" testid="structure-points">
        {hasActions ? (
          <div className="space-y-2.5 max-w-md">
            {Object.entries(labels).map(([key, lbl]) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <span className="text-sm text-[#1A1715]">{lbl}</span>
                <Input data-testid={`pts-${role}-${key}`} type="number" min="0" className="w-24 text-right"
                  value={rc.pts?.[key] ?? 0} onChange={(e) => setPts(key, e.target.value)} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#9A968E]" data-testid="structure-no-actions">
            No automatic earning actions for {role}s yet — award points manually from the Members tab. Tiers, conversion and the store below are fully editable and ready for when earning rules are added.
          </p>
        )}
      </Card>

      {/* Tiers */}
      <Card icon={Trophy} title="Tiers & thresholds" hint="Members reach a tier once total points cross its threshold. The first tier always starts at 0." testid="structure-tiers">
        <div className="space-y-2">
          {rc.levels.map((lv, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`tier-row-${i}`}>
              <Input data-testid={`tier-name-${i}`} className="flex-1" placeholder="Tier name"
                value={lv[0]} onChange={(e) => setLevel(i, 0, e.target.value)} />
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-[#9A968E]">at</span>
                <Input data-testid={`tier-threshold-${i}`} type="number" min="0" className="w-28 text-right"
                  value={lv[1]} disabled={i === 0} onChange={(e) => setLevel(i, 1, e.target.value)} />
                <span className="text-xs text-[#9A968E]">pts</span>
              </div>
              <Button data-testid={`tier-remove-${i}`} size="icon" variant="ghost" className="text-[#7E1119] hover:bg-[#7E1119]/8 shrink-0"
                disabled={rc.levels.length <= 1} onClick={() => removeLevel(i)}><Trash size={16} /></Button>
            </div>
          ))}
        </div>
        <Button data-testid="tier-add" size="sm" variant="outline" className="mt-3 border-[#E9DDC6]" onClick={addLevel}><Plus size={15} className="mr-1" />Add tier</Button>
      </Card>

      {/* Achievements */}
      <Card icon={Medal} title="Achievements" hint="Badges members unlock by hitting a target on a tracked action. Order them from an easy first win to a challenging stretch goal." testid="structure-achievements">
        {(rc.achievements || []).length === 0 && (
          <p className="text-sm text-[#9A968E] mb-3" data-testid="structure-ach-empty">No achievements yet for {role}s. Add one to give members goals to chase.</p>
        )}
        <div className="space-y-3">
          {(rc.achievements || []).map((a, i) => (
            <div key={i} className="border border-[#E9DDC6] rounded-[5px] p-3.5 bg-[#FBFAF7]" data-testid={`ach-row-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-[#1A1715]">
                  <Medal size={15} weight="fill" className="text-[#B08D57]" />{a.label || "New achievement"}
                </span>
                <Button data-testid={`ach-remove-${i}`} size="icon" variant="ghost" className="text-[#7E1119] hover:bg-[#7E1119]/8 h-7 w-7" onClick={() => removeAch(i)}><Trash size={15} /></Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Name"><Input data-testid={`ach-label-${i}`} value={a.label} onChange={(e) => setAch(i, "label", e.target.value)} placeholder="First Shoot" /></Field>
                <Field label="Tracked action">
                  <select data-testid={`ach-metric-${i}`} value={a.metric} onChange={(e) => setAch(i, "metric", e.target.value)}
                    className="w-full h-10 border border-[#E9DDC6] rounded-md px-3 text-sm bg-white">
                    {metricOptions.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Description"><Input data-testid={`ach-desc-${i}`} value={a.desc} onChange={(e) => setAch(i, "desc", e.target.value)} placeholder="Complete your first shoot" /></Field>
                </div>
                <Field label="Target (count to unlock)"><Input data-testid={`ach-target-${i}`} type="number" min="1" value={a.target} onChange={(e) => setAch(i, "target", e.target.value)} /></Field>
              </div>
            </div>
          ))}
        </div>
        <Button data-testid="ach-add" size="sm" variant="outline" className="mt-3 border-[#E9DDC6]" onClick={addAch} disabled={metricOptions.length === 0}><Plus size={15} className="mr-1" />Add achievement</Button>
      </Card>

      {/* Conversion + Redemption store — agents only (they're the paying users) */}
      {role === "agent" ? (<>
      <Card icon={CurrencyDollar} title="Points-to-cash conversion" testid="structure-conversion">
        <div className="flex items-center gap-3 flex-wrap">
          <Input data-testid="points-per-dollar" type="number" min="1" className="w-24 text-right"
            value={rc.points_per_dollar} onChange={(e) => setRc((c) => ({ ...c, points_per_dollar: e.target.value }))} />
          <span className="text-sm text-[#1A1715]">points = <b>$1.00</b> of value</span>
          <span className="text-xs text-[#9A968E]">(shown on member cards & used for cash-value estimates)</span>
        </div>
      </Card>

      {/* Redemption store */}
      <Card icon={Gift} title="Redemption store" hint={`Items ${role}s can spend points on. Add, remove or edit any item.`} testid="structure-store">
        {rc.store.length === 0 && (
          <p className="text-sm text-[#9A968E] mb-3" data-testid="structure-store-empty">No rewards yet for {role}s. Add a placeholder now — members will accrue points and redeem once items go live.</p>
        )}
        <div className="space-y-3">
          {rc.store.map((s, i) => (
            <div key={i} className="border border-[#E9DDC6] rounded-[5px] p-3.5 bg-[#FBFAF7]" data-testid={`store-row-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-[#1A1715]">
                  {s.type === "penthouse" ? <Crown size={15} weight="fill" className="text-[#B08D57]" /> : <CurrencyDollar size={15} weight="duotone" className="text-[#2E4F3B]" />}
                  {s.label || "New reward"}
                </span>
                <Button data-testid={`store-remove-${i}`} size="icon" variant="ghost" className="text-[#7E1119] hover:bg-[#7E1119]/8 h-7 w-7" onClick={() => removeStore(i)}><Trash size={15} /></Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Label"><Input data-testid={`store-label-${i}`} value={s.label} onChange={(e) => setStore(i, "label", e.target.value)} placeholder="$5 shoot credit" /></Field>
                <Field label="Type">
                  <select data-testid={`store-type-${i}`} value={s.type} onChange={(e) => setStore(i, "type", e.target.value)}
                    className="w-full h-10 border border-[#E9DDC6] rounded-md px-3 text-sm bg-white">
                    <option value="credit">Credit / discount</option>
                    <option value="penthouse">Penthouse Partner</option>
                    <option value="cash">Cash payout bonus</option>
                    <option value="giftcard">Gift card</option>
                    <option value="gear">Gear voucher</option>
                    <option value="perk">Perk / experience</option>
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Description"><Input data-testid={`store-desc-${i}`} value={s.desc} onChange={(e) => setStore(i, "desc", e.target.value)} placeholder="Applies to your next shoot invoice" /></Field>
                </div>
                <Field label="Points cost"><Input data-testid={`store-points-${i}`} type="number" min="0" value={s.points} onChange={(e) => setStore(i, "points", e.target.value)} /></Field>
                <Field label="Cash value ($)"><Input data-testid={`store-value-${i}`} type="number" min="0" step="0.01" value={s.value} onChange={(e) => setStore(i, "value", e.target.value)} /></Field>
                {s.type === "penthouse" && (
                  <Field label="Duration (months)"><Input data-testid={`store-months-${i}`} type="number" min="1" value={s.months ?? 12} onChange={(e) => setStore(i, "months", e.target.value)} /></Field>
                )}
              </div>
            </div>
          ))}
        </div>
        <Button data-testid="store-add" size="sm" variant="outline" className="mt-3 border-[#E9DDC6]" onClick={addStore}><Plus size={15} className="mr-1" />Add reward</Button>
      </Card>
      </>) : (
      <Card icon={Gift} title="Redemption store" testid="structure-store-disabled">
        <p className="text-sm text-[#5C5A58]" data-testid="structure-store-statusonly">
          Only <b>agents</b> can redeem points for cash value (they're the paying customers).
          {" "}{role.charAt(0).toUpperCase() + role.slice(1)}s earn points purely for <b>status</b> — tiers, achievements and the leaderboard. There's no redemption store or cash conversion for this role.
        </p>
      </Card>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
        <Button data-testid="structure-reset" variant="outline" className="border-[#E9DDC6] text-[#5C5A58]" onClick={resetDefaults}><ArrowCounterClockwise size={15} className="mr-1.5" />Reset to defaults</Button>
        <Button data-testid="structure-save" className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white" onClick={() => setConfirm(true)}><FloppyDisk size={16} className="mr-1.5" weight="fill" />Save {role} structure</Button>
      </div>

      <Dialog open={confirm} onOpenChange={(o) => !o && !saving && setConfirm(false)}>
        <DialogContent data-testid="structure-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Save {role} rewards structure?</DialogTitle>
            <DialogDescription>
              Members&rsquo; already-earned points stay <b>locked at the current rates</b>. Your new points, tiers and store take effect for <b>activity earned from now on</b>. This can&rsquo;t change history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="structure-confirm-cancel" disabled={saving} onClick={() => setConfirm(false)}>Cancel</Button>
            <Button data-testid="structure-confirm-save" disabled={saving} onClick={save} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{saving ? "Saving…" : "Yes, save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
